'use client';

import { useEffect, useState } from 'react';
import { MaskedSvgIcon } from '@/components/help-center/MaskedSvgIcon';
import type { IconLibraryItem } from '@/lib/icon-library';
import { fetchPublicIcon, fetchPublicIcons } from '@/services/icon-library.service';
import { cn } from '@/lib/utils';

let catalogPromise: Promise<IconLibraryItem[]> | null = null;
const catalogBySlug = new Map<string, IconLibraryItem>();
const detailBySlug = new Map<string, IconLibraryItem | null>();

async function ensureCatalog(): Promise<Map<string, IconLibraryItem>> {
  if (!catalogPromise) {
    catalogPromise = fetchPublicIcons()
      .then((items) => {
        catalogBySlug.clear();
        for (const item of items) catalogBySlug.set(item.slug, item);
        return items;
      })
      .catch(() => {
        catalogPromise = null;
        return [] as IconLibraryItem[];
      });
  }
  await catalogPromise;
  return catalogBySlug;
}

async function resolveIcon(slug: string): Promise<IconLibraryItem | null> {
  const key = slug.trim();
  if (!key) return null;

  if (detailBySlug.has(key)) return detailBySlug.get(key) ?? null;

  const catalog = await ensureCatalog();
  const fromCatalog = catalog.get(key);
  if (fromCatalog?.url) {
    detailBySlug.set(key, fromCatalog);
    return fromCatalog;
  }

  const detail = await fetchPublicIcon(key);
  detailBySlug.set(key, detail);
  return detail;
}

/** Invalida cache (ex.: após CRUD no painel). */
export function invalidateIconLibraryCache() {
  catalogPromise = null;
  catalogBySlug.clear();
  detailBySlug.clear();
}

type IconProps = {
  name: string;
  className?: string;
  title?: string;
};

/**
 * Renderiza um ícone da Icon Library por slug.
 * Fallback seguro se o ícone não existir ou a API falhar.
 */
export function Icon({ name, className, title }: IconProps) {
  const [item, setItem] = useState<IconLibraryItem | null>(() => catalogBySlug.get(name) ?? null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    resolveIcon(name).then((resolved) => {
      if (cancelled) return;
      setItem(resolved);
      if (!resolved) setFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [name]);

  if (failed || !item) {
    return (
      <span
        className={cn(
          'inline-block shrink-0 rounded-sm border border-dashed border-current/30 opacity-40',
          className
        )}
        title={title ?? name}
        aria-hidden
      />
    );
  }

  if (item.renderMode === 'img') {
    return (
      <img
        src={item.url}
        alt=""
        title={title ?? item.name}
        className={cn('inline-block shrink-0 object-contain', className)}
        draggable={false}
        aria-hidden
      />
    );
  }

  return (
    <MaskedSvgIcon
      src={item.url}
      className={cn(className)}
    />
  );
}
